import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { isEmpty } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Box,
  CopyButton,
  DataTable,
  DataTableCellVariants,
  DataTableColumnAlignments,
  DataTableHeaderProps,
  DataTableRowData,
  EmptyState,
  Icon,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { Lottie } from 'components/Animations/SimpleLottie'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useTransactionsQuery } from 'hooks/queries'
import { setPaymentDescription } from 'utils/transactionText'
import { copyToClipboard } from 'utils/utils'

const LatestTransactions = ({ walletId }) => {
  const { t } = useTranslation()

  const {
    data: transactionData,
    isFetching,
    isError,
  } = useTransactionsQuery({
    max: 10,
    offset: 0,
    walletId,
  })
  const latestTransactions = transactionData?.transactions

  const tableColumns: DataTableHeaderProps[] = [
    {
      key: 'currency',
      label: t('latestTransactions.labels.currency'),
      variant: DataTableCellVariants.TEXT,
    },
    {
      key: 'date',
      label: t('latestTransactions.labels.date'),
      variant: DataTableCellVariants.TEXT,
    },
    {
      key: 'transactionId',
      label: t('latestTransactions.labels.transactionId'),
      variant: DataTableCellVariants.TEXT,
    },
    {
      key: 'description',
      label: t('latestTransactions.labels.description'),
      variant: DataTableCellVariants.TEXT,
    },
    {
      key: 'amount',
      label: t('latestTransactions.labels.amount'),
      variant: DataTableCellVariants.TEXT,
    },
    {
      key: 'hash',
      label: t('latestTransactions.labels.hash'),
      variant: DataTableCellVariants.TEXT,
    },
  ]

  const tableTransactionsData: DataTableRowData[] = useMemo(() => {
    return latestTransactions?.map(transaction => {
      const hash = transaction?.hash
        ? transaction?.hash?.includes('offChain')
          ? t('offChain')
          : transaction?.context?.hash
        : ''

      return {
        id: transaction?.id?.toString(),
        cells: [
          {
            key: 'currency',
            variant: DataTableCellVariants.TEXT,
            Avatar: (
              <Avatar type="currency" name={transaction?.wallet?.currency?.code?.toUpperCase()} />
            ),
            Text: transaction?.wallet?.currency?.code,
          },
          {
            key: 'date',
            variant: DataTableCellVariants.TEXT,
            Text: dayjs(transaction.dateCreated).format('DD MMM YYYY'),
            BottomText: dayjs(transaction.dateCreated).format('HH:mm:ss'),
          },
          {
            key: 'transactionId',
            variant: DataTableCellVariants.TEXT,
            Text: transaction?.id?.toString(),
          },
          {
            key: 'description',
            variant: DataTableCellVariants.TEXT,
            Text: setPaymentDescription(transaction),
          },
          {
            key: 'amount',
            variant: DataTableCellVariants.TEXT,
            Text: transaction?.amount?.toString(),
            align: DataTableColumnAlignments.RIGHT,
          },
          {
            key: 'hash',
            variant: DataTableCellVariants.TEXT,
            Text: hash ? ellipseMiddleText(hash, 10, 10) : '-',
            Actions: [<CopyButton key={`copy-${hash}`} copyText={hash} />],
          },
        ],
      }
    })
  }, [latestTransactions, ellipseMiddleText, copyToClipboard])

  if (isFetching)
    return (
      <Box width="full" flex flexCol>
        <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
      </Box>
    )

  if (isError)
    return (
      <EmptyState
        testId="empty-state"
        LottiePlayer={<Lottie src="connection-error" />}
        title={t('latestTransactions.error')}
      />
    )

  if (isEmpty(latestTransactions))
    return (
      <EmptyState
        testId="empty-state"
        LottiePlayer={<Lottie src="no-data" />}
        title={t('latestTransactions.empty')}
      />
    )

  return <DataTable headers={tableColumns} data={tableTransactionsData} />
}

export default LatestTransactions
